import { NodeFlags, type IHierarchyNode } from "./hierarchy.js";

export interface VisibleHierarchyRow {
    readonly node: IHierarchyNode;
    readonly level: number;
    readonly parentIsVisible: boolean;
    readonly parentIsInactive: boolean;
}

export interface FlattenVisibleHierarchyOptions {
    /** When present, search every expanded and collapsed branch and return matches only. */
    readonly matches?: (node: IHierarchyNode) => boolean;
}

/** The hierarchy as visible rows, with the same pruning and ancestor state as the tree UI. */
export function flattenVisibleHierarchy(
    roots: readonly IHierarchyNode[],
    options: FlattenVisibleHierarchyOptions = {},
    previous?: VisibleHierarchyRow[],
): VisibleHierarchyRow[] {
    const rows: VisibleHierarchyRow[] = [];
    const searching = options.matches !== undefined;

    const visit = (
        node: IHierarchyNode,
        level: number,
        parentIsVisible: boolean,
        parentIsInactive: boolean,
    ): void => {
        if ((node.flags & NodeFlags.HideInHierarchy) !== 0) return;

        if (!searching || options.matches!(node)) {
            const old = previous?.[rows.length];
            rows.push(old && old.node === node && old.level === level
                && old.parentIsVisible === parentIsVisible && old.parentIsInactive === parentIsInactive
                ? old : { node, level, parentIsVisible, parentIsInactive });
        }

        if (!searching && (!node.expanded || !node.hasChildren)) return;
        const visible = node.visible && parentIsVisible;
        const inactive = (node.flags & NodeFlags.Inactive) !== 0 || parentIsInactive;
        for (const child of node.children) {
            visit(child, level + 1, visible, inactive);
        }
    };

    for (const root of roots) visit(root, 0, true, false);
    return previous && previous.length === rows.length && rows.every((row, index) => row === previous[index]) ? previous : rows;
}
